// src/screens/SmartLockPage.jsx
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Center,
  Flex,
  Button,
  TextInput,
  Text,
  Paper,
  Title,
  Loader,
  Space,
  Box,
  Image,
} from '@mantine/core';
import { FaTools } from "react-icons/fa";
import Confetti from 'react-confetti';
import Lottie from 'lottie-react';
import PhoneInput from 'react-phone-number-input';
import { GooglePlayButton, AppStoreButton } from 'react-mobile-app-button';

// Lottie JSONs
import wclogoAnimation from '../assets/lottie/wclogo.json';
import signalAnimation from '../assets/lottie/signal.json';
import unlockingAnimation from '../assets/lottie/unlocking.json';
import unlockedAnimation from '../assets/lottie/unlocked.json';
import errorAnimation from '../assets/lottie/error.json';
import verifyAnimation from '../assets/lottie/verify.json';
import imagePlaceholder from '../assets/lottie/imgplaceholder.json';

// Static images
import czbackground from '../assets/images/czbackground.png';

import { CONFIG } from '../atoms/config';
import {
  checkUserAuth as legacyCheckUserAuth,
  confirmVerificationCode as legacyConfirmVerificationCode,
  unlockAgain as legacyUnlockAgain,
  activateHiredSession,
  deactivateHiredSession,
} from '../atoms/network';
import { DotNetAPI } from '../atoms/dotnetAPI';
import moment from 'moment-timezone';

import StartHireView from './StartHireView';
import ReportDamageFlow from '../atoms/ReportDamageFlow';

// ─────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────
const S = {
  fullHeightContainer: { minHeight: '100vh' },
  fullScreenContainer: { position: 'relative', minHeight: '100vh', overflow: 'hidden' },
  fullHeightCenter: {
    height: "100%",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    gap: 16,
  },
  logoCircleWrapper: (size = 175) => ({
    position: 'relative',
    width: size,
    height: size,
    marginTop: 110,
    zIndex: 1,
  }),
  logoCircleBg: (size = 175) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: size,
    height: size,
    borderRadius: '50%',
    backgroundColor: 'white',
    zIndex: 0,
  }),
  labelLeftAlign: { style: { textAlign: 'left' } },
  paperMax: { maxWidth: 480, marginInline: 'auto', textAlign: 'left' },
  unlockedBox: { textAlign: 'center', zIndex: 1, alignItems: 'center', width: '100%', maxWidth: 520 },
  lottieUnlocked: { width: 150, height: 150 },
  lottieVerify: { width: 200, height: 230, marginTop: 16 },
  lottieError: { width: 200, height: 200 },
  lottieSignal: { opacity: 0.5, width: 600, height: 600 },
  lottieUnlocking: { width: 150, height: 150 },
  bgImage: { position: 'absolute', top: 0, left: 0, zIndex: 0 },
};

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────
function truthy(v) {
  return v === true || v === 1 || v === '1' || v === 'true';
}

function toNumber(value, fallback = 0) {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : fallback;
}

function formatPhoneNumber(raw) {
  if (!raw) return '';

  if (String(raw).startsWith('+')) {
    return String(raw).replace(/\s+/g, '');
  }

  let numberValue = String(raw).replace(/\D+/g, '');

  if (numberValue.startsWith('0')) {
    numberValue = `+61${numberValue.slice(1)}`;
  }

  if (!numberValue.startsWith('+')) {
    numberValue = `+${numberValue}`;
  }

  return numberValue;
}

function apiStatusIsSuccess(json) {
  if (json?.status === true) return true;
  if (json?.status === 1) return true;
  if (json?.status === '1') return true;
  if (String(json?.status).toLowerCase() === 'true') return true;

  return false;
}

function getApiMessage(json, fallback = 'Something went wrong.') {
  return json?.message || json?.error || fallback;
}

function detectBrowserInfo() {
  if (typeof navigator === 'undefined') {
    return { browser: 'generic', os: 'desktop' };
  }

  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';
  const maxTouchPoints = navigator.maxTouchPoints || 0;

  const isIOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Mac/i.test(platform) && maxTouchPoints > 1);

  const isAndroid = /Android/i.test(ua);
  const isMac = /Macintosh|Mac OS X/i.test(ua) && !isIOS;
  const isWindows = /Windows/i.test(ua);

  let browser = 'generic';

  if (/CriOS|Mozilla/i.test(ua)) {
    browser = 'chrome';
  } else if (/EdgiOS|EdgA|Edg/i.test(ua)) {
    browser = 'edge';
  } else if (/FxiOS|Firefox/i.test(ua)) {
    browser = 'firefox';
  } else if (/OPiOS|OPR|Opera/i.test(ua)) {
    browser = 'opera';
  } else if (
    /Safari/i.test(ua) &&
    !/CriOS|Chrome|EdgiOS|Edg|FxiOS|Firefox|OPiOS|OPR|Opera/i.test(ua)
  ) {
    browser = 'safari';
  }

  console.log(ua, 'yoyoyoyoyowwww');

  let os = 'desktop';
  if (isIOS) os = 'ios';
  else if (isAndroid) os = 'android';
  else if (isMac) os = 'mac';
  else if (isWindows) os = 'windows';

  return { browser, os };
}

function getLocationHelpContent({ browserInfo, permissionState, errorCode }) {
  const denied = permissionState === 'denied' || errorCode === 1;
  const { browser, os } = browserInfo || { browser: 'generic', os: 'desktop' };

  if (browser === 'chrome' && os === 'ios') {
    return {
      title: denied ? 'Location Access Denied' : 'Location Not Confirmed',
      message: denied
        ? 'Location access was denied in Chrome. We cannot show the browser prompt again until you change the permission settings for Chrome and this website.'
        : 'We could not confirm your location yet. Please allow location access in Chrome and try again.',
      steps: [
        'Open Settings on your iPhone',
        'Go to Privacy & Security > Location Services and make sure Location Services is on',
        'Scroll down to Chrome, tap Location, then choose Ask Next Time or While Using the App',
        'Open Chrome, tap More, then Settings > Content Settings',
        'Check the location permission for sites, then return here and tap Try again',
      ],
      buttonLabel: 'How to enable',
    };
  }

  if (browser === 'safari' && os === 'ios') {
    return {
      title: denied ? 'Location Access Denied' : 'Location Not Confirmed',
      message: denied
        ? 'Location access was denied in Safari. We cannot show the browser prompt again until you change the setting for Safari and this website.'
        : 'We could not confirm your location yet. Please allow location access in Safari and try again.',
      steps: [
        'Open Settings on your iPhone',
        'Go to Privacy & Security > Location Services and make sure Location Services is on',
        'Scroll to Safari Websites and choose Ask Next Time or While Using the App',
        'Open Safari and return to this page',
        'Tap the Page Menu button on the left side of the address bar, open Website Settings, and change Location to Ask or Allow',
        'Come back here and tap Try again',
      ],
      buttonLabel: 'How to enable',
    };
  }

  if (browser === 'chrome' && os === 'android') {
    return {
      title: denied ? 'Location Access Denied' : 'Location Not Confirmed',
      message: denied
        ? 'Location access was denied in Chrome. We cannot show the browser prompt again until you change the permission for this site.'
        : 'We could not confirm your location yet. Please allow location access in Chrome and try again.',
      steps: [
        'Make sure Location is turned on for your phone',
        'Open Chrome and go to this site',
        'Tap the site info icon to the left of the address bar, then tap Permissions',
        'Change Location to Allow',
        'Return to this page and tap Try again',
      ],
      buttonLabel: 'How to enable',
    };
  }

  if (browser === 'chrome' && (os === 'mac' || os === 'windows' || os === 'desktop')) {
    return {
      title: denied ? 'Location Access Denied' : 'Location Not Confirmed',
      message: denied
        ? 'Location access was denied in Chrome. We cannot show the browser prompt again until you change the site permission.'
        : 'We could not confirm your location yet. Please allow location access in Chrome and try again.',
      steps: [
        'Open Chrome Settings',
        'Go to Privacy and security > Site settings > Location',
        'Allow location access, or remove the blocked rule for this site',
        'Return to this page and tap Try again',
      ],
      buttonLabel: 'How to enable',
    };
  }

  if (browser === 'edge') {
    return {
      title: denied ? 'Location Access Denied' : 'Location Not Confirmed',
      message: denied
        ? 'Location access was denied in Edge. We cannot show the browser prompt again until you change the site permission.'
        : 'We could not confirm your location yet. Please allow location access in Edge and try again.',
      steps: [
        'Make sure Location Services are turned on for your device',
        'Open Edge site permissions for this page',
        'Change Location to Ask or Allow',
        'Return to this page and tap Try again',
      ],
      buttonLabel: 'How to enable',
    };
  }

  if (browser === 'firefox') {
    return {
      title: denied ? 'Location Access Denied' : 'Location Not Confirmed',
      message: denied
        ? 'Location access was denied in Firefox. We cannot show the browser prompt again until you change the site permission.'
        : 'We could not confirm your location yet. Please allow location access in Firefox and try again.',
      steps: [
        'Make sure Location Services are turned on for your device',
        'Open Firefox site permissions for this page',
        'Change Location to Ask or Allow',
        'Return to this page and tap Try again',
      ],
      buttonLabel: 'How to enable',
    };
  }

  return {
    title: denied ? 'Location Access Denied' : 'Location Not Confirmed',
    message: denied
      ? 'Location access was denied for this browser. We cannot show the browser prompt again until you change the site permission.'
      : 'We could not confirm your location yet. Please allow location access for this browser and try again.',
    steps: [
      'Make sure Location Services are turned on for your device',
      'Open this browser’s site permissions for the current page',
      'Change Location to Ask or Allow',
      'Return to this page and tap Try again',
    ],
    buttonLabel: 'How to enable',
  };
}

// Geolocation helpers
function getPositionOnce(opts = {}) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, opts);
  });
}

function raceOnceWithWatch(opts = {}) {
  return new Promise((resolve, reject) => {
    let settled = false;
    let watchId = null;

    const clear = () => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
    };

    try {
      watchId = navigator.geolocation.watchPosition(
        (pos) => {
          if (!settled) {
            settled = true;
            clear();
            resolve(pos);
          }
        },
        () => { },
        opts
      );
    } catch {
      // ignore
    }

    getPositionOnce(opts)
      .then((pos) => {
        if (!settled) {
          settled = true;
          clear();
          resolve(pos);
        }
      })
      .catch((err) => {
        if (!settled) {
          settled = true;
          clear();
          reject(err);
        }
      });
  });
}

async function getBestEffortPosition({
  attempts = 3,
  baseDelayMs = 900,
  preciseOpts = { enableHighAccuracy: true, timeout: 12000, maximumAge: 15000 },
  coarseOpts = { enableHighAccuracy: false, timeout: 12000, maximumAge: 60000 },
} = {}) {
  let lastErr = null;

  for (let i = 0; i < attempts; i++) {
    try {
      const pos = await raceOnceWithWatch(preciseOpts);
      return { pos, coarse: false };
    } catch (err) {
      lastErr = err;
      const isTransient =
        err && (err.code === 2 || err.message?.includes('kCLErrorLocationUnknown'));
      if (!isTransient) break;
      const wait = baseDelayMs * Math.pow(1.5, i);
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, wait));
    }
  }

  try {
    const pos = await raceOnceWithWatch(coarseOpts);
    return { pos, coarse: true };
  } catch (err) {
    try {
      const pos = await getPositionOnce({
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: Infinity,
      });
      return { pos, coarse: true };
    } catch (err2) {
      throw lastErr || err || err2;
    }
  }
}

function getDistanceFromLatLonInM(lat1, lon1, lat2, lon2) {
  const R = 6371e3;
  const toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function isWithinAllowedRadius({ user, target, accuracy, coarse }) {
  const baseRadius = 250;
  const coarseRadius = 1500;
  const dist = getDistanceFromLatLonInM(user.lat, user.lng, target.lat, target.lng);
  const useRelaxed = coarse || (typeof accuracy === 'number' && accuracy > 800);
  const allowed = useRelaxed ? coarseRadius : baseRadius;
  return { ok: dist <= allowed, dist, allowed, relaxed: useRelaxed };
}

// ─────────────────────────────────────────────────────────────
// Auth flow states
// ─────────────────────────────────────────────────────────────
const AuthFlow = {
  UNKNOWN: 'UNKNOWN',
  NO_LOCK: 'NO_LOCK',
  NO_LOCATION: 'NO_LOCATION',
  INPUT_DETAILS: 'INPUT_DETAILS',
  LOADING: 'LOADING',
  ENTER_OTP: 'ENTER_OTP',
  UNLOCKED: 'UNLOCKED',
  EXPIRED: 'EXPIRED',
  NOT_NEAR_LOCATION: 'NOT_NEAR_LOCATION',
  HIRE_SELECT: 'HIRE_SELECT',
};

// ─────────────────────────────────────────────────────────────
// Small components
// ─────────────────────────────────────────────────────────────
const UnlockedLottie = React.memo(
  function UnlockedLottie() {
    const lottieRef = useRef(null);

    useEffect(() => {
      lottieRef.current?.play?.();
    }, []);

    return (
      <Lottie
        lottieRef={lottieRef}
        animationData={unlockedAnimation}
        loop={false}
        autoplay
        style={S.lottieUnlocked}
      />
    );
  },
  () => true
);

const LogoWithCircle = React.memo(function LogoWithCircle({
  animationData = null,
  isLoading = false,
  fallbackAnimation = wclogoAnimation,
}) {
  const size = 175;

  const shouldShowPlaceholder = isLoading || (!animationData && !fallbackAnimation);
  const resolvedAnimation = shouldShowPlaceholder
    ? imagePlaceholder
    : animationData || fallbackAnimation;
  const shouldLoop = shouldShowPlaceholder;

  if (!resolvedAnimation) {
    return null;
  }

  return (
    <Flex justify="center" align="center" style={S.logoCircleWrapper(size)}>
      <Box style={S.logoCircleBg(size)} />
      <Lottie
        animationData={resolvedAnimation}
        loop={shouldLoop}
        autoplay
        style={{
          position: 'relative',
          width: size - 16,
          height: size - 16,
          zIndex: 2,
          paddingRight: 5,
        }}
      />
    </Flex>
  );
});

const RemainingTimeDisplay = React.memo(function RemainingTimeDisplay({ remainingTime }) {
  return (
    <Text size="lg" mt="sm" ta="center">
      Remaining Time: {remainingTime} seconds
    </Text>
  );
});

const EnterOtpView = React.memo(function EnterOtpView({ onConfirmOTP, verificationCodeErr }) {
  const [verificationCode, setVerificationCode] = useState('');
  const onConfirm = () => onConfirmOTP(verificationCode);

  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" w="100%">
          <Center>
            <Lottie animationData={verifyAnimation} style={S.lottieVerify} loop={false} />
          </Center>

          <Center>
            <Title order={3} align="center">
              Enter Verification Code
            </Title>
          </Center>

          <Space h="md" />

          <TextInput
            label="Verification Code"
            placeholder="123456"
            size="md"
            type="tel"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.currentTarget.value)}
            labelProps={S.labelLeftAlign}
          />

          {verificationCodeErr && (
            <Text color="red" mt="md">
              {verificationCodeErr}
            </Text>
          )}

          <Space h="md" />

          <Button size="md" fullWidth onClick={onConfirm}>
            Confirm Code
          </Button>
        </Flex>
      </Center>
    </Container>
  );
});

const LoadingView = React.memo(function LoadingView() {
  return (
    <Container fluid px={0} style={S.fullScreenContainer}>
      <Center style={{ ...S.fullHeightCenter, position: 'relative' }}>
        <Flex justify="center" direction="column">
          <Flex
            style={{
              position: 'relative',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <Lottie style={S.lottieSignal} animationData={signalAnimation} loop />
            <Flex
              style={{
                position: 'absolute',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Flex direction="column" align="center" w="100%">
                <Lottie style={S.lottieUnlocking} animationData={unlockingAnimation} loop />
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </Center>
    </Container>
  );
});

const NoLockView = React.memo(function NoLockView({
  title = 'No App Clip Scanned',
  message = "You haven't scanned a QR code yet. Please scan the QR code on the smart lock to begin your access.",
}) {
  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center">
          <Lottie animationData={errorAnimation} style={S.lottieError} loop={false} />
          <Title>{title}</Title>
          <Text align="center" style={{ maxWidth: 300 }}>
            {message}
          </Text>
        </Flex>
      </Center>
    </Container>
  );
});

const NoLocationView = React.memo(function NoLocationView({
  onRetry,
  browserInfo,
  permissionState,
  locationErrorCode,
}) {
  const [showHelp, setShowHelp] = useState(locationErrorCode === 1 || permissionState === 'denied');

  useEffect(() => {
    if (locationErrorCode === 1 || permissionState === 'denied') {
      setShowHelp(true);
    }
  }, [locationErrorCode, permissionState]);

  const help = useMemo(
    () =>
      getLocationHelpContent({
        browserInfo,
        permissionState,
        errorCode: locationErrorCode,
      }),
    [browserInfo, permissionState, locationErrorCode]
  );

  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center" gap="sm" w="100%">
          <Lottie animationData={errorAnimation} style={S.lottieError} loop={false} />

          <Title style={{ textAlign: 'center' }}>{help.title}</Title>

          <Text align="center" style={{ maxWidth: 360 }}>
            {help.message}
          </Text>

          <Flex gap="sm" mt="sm" wrap="wrap" justify="center">
            <Button variant="default" onClick={() => setShowHelp((v) => !v)}>
              {showHelp ? 'Hide help' : help.buttonLabel}
            </Button>

            <Button variant="outline" onClick={onRetry}>
              Try again
            </Button>
          </Flex>

          {showHelp && (
            <Paper withBorder p="md" mt="sm" radius="md" style={{ width: '100%', maxWidth: 420 }}>
              <Text fw={600} mb="xs">
                Steps
              </Text>

              {help.steps.map((step, index) => (
                <Text key={index} size="sm" c="dimmed" mb={index === help.steps.length - 1 ? 0 : 8}>
                  {index + 1}. {step}
                </Text>
              ))}
            </Paper>
          )}
        </Flex>
      </Center>
    </Container>
  );
});

const NotNearLocationView = React.memo(function NotNearLocationView() {
  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center">
          <Lottie animationData={errorAnimation} style={S.lottieError} loop={false} />
          <Title style={{ textAlign: 'center' }}>Invalid Location</Title>
          <Text align="center" style={{ maxWidth: 300 }}>
            We found that you are not near the location of the item, please try again later.
          </Text>
        </Flex>
      </Center>
    </Container>
  );
});

const ExpiredView = React.memo(function ExpiredView() {
  return (
    <Container size={700} px="md" style={{ height: '100vh', background: 'linear-gradient(white, #f2f2f2)' }}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center">
          <Lottie animationData={errorAnimation} style={S.lottieError} loop={false} />
          <Title>Access Expired</Title>
          <Text align="center" style={{ maxWidth: 300, marginTop: 8 }}>
            Your temporary access period has ended. If you need access again, scan the QR code or call Council.
          </Text>
        </Flex>
      </Center>
    </Container>
  );
});

const InputDetailsView = React.memo(function InputDetailsView({
  site,
  lock,
  isCareZenBusiness = false,
  isBanned,
  logoAnimation,
  isLogoAnimationLoading,
  name,
  setName,
  phoneNumber,
  setPhoneNumber,
  errorMsg,
  supportPhone,
  onSubmit,
}) {
  const backgroundUrl = useMemo(
    () => (isCareZenBusiness ? czbackground : site?.image_remote_url || null),
    [isCareZenBusiness, site?.image_remote_url]
  );

  const welcomeTitle = isCareZenBusiness ? 'CareZen' : site?.title;
  const itemTitle = isCareZenBusiness ? 'Virtual Care Pod' : lock?.title;

  const imageHeight = '220px';

  const pricePerMin = useMemo(() => {
    const n = lock?.hire_cost_base;

    if (typeof n !== 'number' || Number.isNaN(n)) return null;

    const f = new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return `${f.format(n)} per/min`;
  }, [lock?.hire_cost_base]);

  const maxMins = useMemo(() => {
    const v = lock?.hire_max_length;
    return typeof v === 'number' && !Number.isNaN(v) ? `${v} minutes` : null;
  }, [lock?.hire_max_length]);

  return (
    <Container
      key="input-details"
      size={700}
      px="md"
      style={{ ...S.fullHeightContainer, position: 'relative', overflow: 'hidden' }}
    >
      <Flex justify="center">
        {backgroundUrl && (
          <Image src={backgroundUrl} width="100%" height={imageHeight} style={S.bgImage} />
        )}

        <LogoWithCircle
          animationData={logoAnimation}
          isLoading={isLogoAnimationLoading}
          fallbackAnimation={null}
        />
      </Flex>

      <Center style={{ paddingTop: 12, paddingBottom: 100 }}>
        <Box style={{ textAlign: 'center', width: '100%' }}>
          <Title order={3}>Welcome to {welcomeTitle}</Title>
          <Text size="sm" c="dimmed">{itemTitle}</Text>

          {lock?.is_ajax_item && (pricePerMin || maxMins) && (
            <Flex align="center" gap={8} justify="center" mt={6} c="dimmed">
              {pricePerMin && <Text size="xs">{pricePerMin}</Text>}
              {pricePerMin && maxMins && <Text size="xs">•</Text>}
              {maxMins && <Text size="xs">{maxMins}</Text>}
            </Flex>
          )}

          <Space h={28} />

          <Box style={{ maxWidth: '100%', marginInline: 'auto', textAlign: 'left' }}>
            <TextInput
              placeholder="Enter Name (Optional)"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              mb="sm"
              size="md"
              labelProps={S.labelLeftAlign}
            />

            <PhoneInput
              international
              defaultCountry="AU"
              countryCallingCodeEditable={false}
              placeholder="Enter phone number"
              value={phoneNumber}
              onChange={setPhoneNumber}
              inputComponent={TextInput}
              size="md"
              inputProps={{ size: 'md', mb: 'md', labelProps: S.labelLeftAlign }}
            />

            {errorMsg && (
              <Text color="red" size="sm" mb="md">
                {errorMsg}
              </Text>
            )}

            {isBanned && supportPhone && (
              <Button
                component="a"
                href={"tel:" + supportPhone}
                color="green"
                size="md"
                mt="md"
                fullWidth
              >
                Call Support
              </Button>
            )}

            <Button size="md" mt="md" fullWidth onClick={onSubmit}>
              {lock?.is_ajax_item ? 'Next' : 'Unlock Door'}
            </Button>
          </Box>

          <Space h="md" />

          <Text size="xs" style={{ color: '#999' }}>
            By continuing, you agree to our{' '}
            <a href="https://www.autech.com.au/unlockwc-privacy">Privacy Policy</a> and{' '}
            <a href="https://www.autech.com.au/unlockwc-tc">T&amp;Cs</a>.
          </Text>
        </Box>
      </Center>
    </Container>
  );
});

const UnlockedView = React.memo(function UnlockedView({
  isAjaxItem,
  isHireStarted,
  onStartHire,
  startingHire,
  onUnlockAgain,
  unlocking,
  remainingTime,
  hireMaxLength,
  sessionPurchasedTime = 0,
  errorMsg,
  setAuthFlow,
  AuthFlowRef,
  showConfetti,
  setHireMode,
  supportPhone,
  onReportDamage,
  hasReportDamage
}) {
  const extendAvailable = useMemo(
    () => Math.max(0, Number(hireMaxLength || 0) - Number(sessionPurchasedTime || 0)),
    [hireMaxLength, sessionPurchasedTime]
  );

  return (
    <Container size={700} px="md" fluid>
      {showConfetti && <Confetti recycle={false} />}

      <Center>
        <Box style={S.unlockedBox}>
          <Flex justify="center" mt="64px">
            <UnlockedLottie />
          </Flex>

          {isAjaxItem ? (
            <Title order={2} mt="xs">Item Hired</Title>
          ) : (
            <Title order={2} mt="xs">Door Unlocked</Title>
          )}

          {(!isAjaxItem || (isAjaxItem && isHireStarted)) && (
            <RemainingTimeDisplay remainingTime={remainingTime} />
          )}

          <Paper shadow="xs" p="md" mt="md" sx={S.paperMax}>
            {isAjaxItem ? (
              <>
                <Text size="sm" mb="sm">
                  1. Start your hire by pressing the green button below.<br />
                  2. Once your hire is started, you can use the hot water for the paid amount of time.<br />
                  3. Keep your phone handy in case you need support.
                </Text>

                {!isHireStarted && (
                  <Button
                    fullWidth
                    onClick={onStartHire}
                    color="green"
                    mt="sm"
                    loading={startingHire}
                    disabled={startingHire}
                  >
                    Start Hire
                  </Button>
                )}

                {isHireStarted && !isAjaxItem && (
                  <Button
                    fullWidth
                    onClick={onUnlockAgain}
                    color="blue"
                    mt="sm"
                    loading={unlocking}
                    disabled={unlocking}
                  >
                    Unlock Again
                  </Button>
                )}

                {extendAvailable > 0 && (
                  <Button
                    variant="light"
                    fullWidth
                    color="orange"
                    mt="sm"
                    onClick={() => {
                      setHireMode('extend');
                      setAuthFlow(AuthFlowRef.HIRE_SELECT);
                    }}
                  >
                    Extend
                  </Button>
                )}

                {errorMsg && <Text c="red" size="sm" mt="sm">{errorMsg}</Text>}

                {supportPhone && (
                  <Button variant="outline" fullWidth mt="sm" onClick={() => window.open('tel://' + supportPhone)}>
                    Call Support
                  </Button>
                )}

                {hasReportDamage && (
                  <Button variant="outline" color="red" fullWidth mt="sm" onClick={onReportDamage}>
                    Report Damage
                  </Button>
                )}
              </>
            ) : (
              <>
                <Text size="sm" mb="sm">
                  You now have temporary access to the facility. This access is valid for a limited
                  time. For assistance, call support.
                </Text>

                <Button
                  fullWidth
                  onClick={onUnlockAgain}
                  color="blue"
                  mt="sm"
                  loading={unlocking}
                  disabled={unlocking}
                >
                  Unlock Again
                </Button>

                {errorMsg && <Text c="red" size="sm" mt="sm">{errorMsg}</Text>}

                {supportPhone && (
                  <Button variant="outline" fullWidth mt="sm" onClick={() => window.open('tel://' + supportPhone)}>
                    Call Support
                  </Button>
                )}

                {hasReportDamage && (
                  <Button variant="outline" color="red" fullWidth mt="sm" onClick={onReportDamage}>
                    Report Damage
                  </Button>
                )}
              </>
            )}
          </Paper>
        </Box>
      </Center>
    </Container>
  );
});

const MembershipOnlyView = React.memo(function MembershipOnlyView({
  title = 'Members-only access',
  message = 'This facility can only be accessed by member users. Please download our full app to access this.',
  appleLink = '',
  googleLink = '',
  logoAnimation = null,
  isLogoAnimationLoading = false,
}) {
  const hasApple = typeof appleLink === 'string' && appleLink.trim().length > 0;
  const hasGoogle = typeof googleLink === 'string' && googleLink.trim().length > 0;

  return (
    <Container size={700} px="md" style={S.fullHeightContainer}>
      <Center style={S.fullHeightCenter}>
        <Flex direction="column" align="center" justify="center" gap="sm" w="100%">
          <LogoWithCircle
            animationData={logoAnimation}
            isLoading={isLogoAnimationLoading}
            fallbackAnimation={null}
          />

          <Title ta="center">{title}</Title>

          <Text ta="center" c="dimmed" style={{ maxWidth: 360 }}>
            {message}
          </Text>

          <Flex mt="md" gap="md" wrap="wrap" justify="center" w="100%" style={{ maxWidth: 420 }}>
            {hasApple && (
              <AppStoreButton
                url={appleLink}
                theme="dark"
                className="custom-style"
                width="auto"
              />
            )}

            {hasGoogle && (
              <GooglePlayButton
                url={googleLink}
                theme="dark"
                className="custom-style"
                width="auto"
              />
            )}
          </Flex>
        </Flex>
      </Center>
    </Container>
  );
});

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────
export default function SmartLockPage() {
  const { siteID, itemID } = useParams();

  const [authFlow, setAuthFlow] = useState(AuthFlow.UNKNOWN);
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [verificationCodeErr, setVerificationCodeErr] = useState('');
  const [remainingTime, setRemainingTime] = useState(300);
  const [timerId, setTimerId] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [site, setSite] = useState({});
  const [lock, setLock] = useState({});
  const [invalidLock, setInvalidLock] = useState(false);
  const [isBanned, setIsBanned] = useState(false);
  const [isOutage, setIsOutage] = useState(false);
  const [logoAnimation, setLogoAnimation] = useState(null);
  const [isLogoAnimationLoading, setIsLogoAnimationLoading] = useState(true);

  const [appStoreLink, setAppStoreLink] = useState('');
  const [googlePlayStoreLink, setGooglePlayStoreLink] = useState('');
  const [isMembershipOnly, setIsMembershipOnly] = useState(false);

  const [showReportDamage, setShowReportDamage] = useState(false);
  const [hasReportDamage, setHasReportDamage] = useState(false);

  const [sessionId, setSessionId] = useState(null);
  const [sessionLength, setSessionLength] = useState(0);
  const [isHireStarted, setIsHireStarted] = useState(false);
  const [startingHire, setStartingHire] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [hireMode, setHireMode] = useState('initial');
  const [supportPhone, setSupportPhone] = useState(null);
  const [businessTitle, setBusinessTitle] = useState('');
  const [businessId, setBusinessId] = useState(null);
  const [stripePublishableKey, setStripePublishableKey] = useState(null);

  const [browserInfo] = useState(() => detectBrowserInfo());
  const [geoPermissionState, setGeoPermissionState] = useState('unknown');
  const [locationErrorCode, setLocationErrorCode] = useState(null);

  const isCareZenBusiness = Number(businessId) === 20;
  const shouldUseDotNetCustomerFlow = Number(businessId) === 20 && !lock?.is_ajax_item;

  useEffect(() => {
    if (showConfetti) {
      const timer = setTimeout(() => setShowConfetti(false), 1800);
      return () => clearTimeout(timer);
    }

    return undefined;
  }, [showConfetti]);

  useEffect(() => {
    if (!itemID) {
      setAuthFlow(AuthFlow.NO_LOCK);
      return;
    }

    setLogoAnimation(null);
    setIsLogoAnimationLoading(true);
    getSiteData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemID]);

  useEffect(() => {
    if (Object.values(site).length > 0) {
      requestLocationCheck();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site]);

  function startCountdown() {
    if (timerId) clearInterval(timerId);

    const id = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          setAuthFlow(AuthFlow.EXPIRED);
          onEndHire();
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    setTimerId(id);
  }

  useEffect(() => {
    if (authFlow === AuthFlow.UNLOCKED && !lock?.is_ajax_item) {
      startCountdown();

      return () => {
        if (timerId) clearInterval(timerId);
      };
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFlow, lock?.is_ajax_item]);

  useEffect(() => {
    if (authFlow === AuthFlow.UNLOCKED && lock?.is_ajax_item && isHireStarted && !timerId) {
      startCountdown();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authFlow, lock?.is_ajax_item, isHireStarted]);

  useEffect(() => {
    if (authFlow !== AuthFlow.UNLOCKED && timerId) {
      clearInterval(timerId);
      setTimerId(null);
    }
  }, [authFlow, timerId]);

  async function syncGeolocationPermission() {
    try {
      if (navigator.permissions?.query) {
        const perm = await navigator.permissions.query({ name: 'geolocation' });
        setGeoPermissionState(perm.state || 'unknown');
        return perm.state || 'unknown';
      }
    } catch {
      // ignore
    }

    return 'unknown';
  }

  function applyLoadedSitePayload(data = {}, fallbackBusinessId = 0) {
    if (truthy(data.is_membership)) {
      setHasReportDamage(false);
      setIsMembershipOnly(true);
      setGooglePlayStoreLink(data?.google_link ?? '');
      setAppStoreLink(data?.apple_link ?? data?.apple ?? '');

      setAuthFlow(AuthFlow.NO_LOCK);
      setErrorMsg('This facility can only be accessed by member users, please download our full app to access this');
      setInvalidLock(true);
      setLogoAnimation(data.logo_animation || null);
      return;
    }

    const nextSite = data.site || {};
    const nextItem = data.item || {};

    if (!Object.keys(nextSite).length || !Object.keys(nextItem).length) {
      setHasReportDamage(false);
      setAuthFlow(AuthFlow.NO_LOCK);
      setErrorMsg('Could not load venue.');
      setInvalidLock(true);
      setLogoAnimation(null);
      return;
    }

    const normalisedItem = {
      ...nextItem,
      is_ajax_item: truthy(nextItem.is_ajax_item),
      is_outage: truthy(nextItem.is_outage),
      hire_length: toNumber(nextItem.hire_length ?? nextItem.min_hire_length, 0),
      hire_max_length: toNumber(nextItem.hire_max_length ?? nextItem.max_hire_length, 0),
      hire_cost_base: toNumber(nextItem.hire_cost_base ?? nextItem.cost, 0),
    };

    const outage = truthy(normalisedItem.is_outage);
    setIsOutage(outage);

    if (outage) {
      setHasReportDamage(false);
      setSite({});
      setLock({});
      setLogoAnimation(null);
      return;
    }

    const nextBusinessId = toNumber(
      data.business_id
        ?? data.businessId
        ?? nextSite.business_id
        ?? nextSite.businessId
        ?? nextItem.business_id
        ?? nextItem.businessId,
      fallbackBusinessId,
    );

    if (nextBusinessId > 0) {
      setBusinessId(nextBusinessId);
    }

    const nextStripeKey =
      data.stripe_key
      || data.stripe_public_key
      || data.stripePublishableKey
      || '';

    if (nextStripeKey) {
      setStripePublishableKey(nextStripeKey);
    }

    setHasReportDamage(
      truthy(data.has_damage_report ?? data.hasReportDamage ?? data.has_report_damage)
    );

    setSite(nextSite);
    setLock(normalisedItem);
    setLogoAnimation(data.logo_animation || null);
    setSupportPhone(data.support_phone || null);
    setBusinessTitle(data.business_title || '');
    setInvalidLock(false);
    setIsMembershipOnly(false);
  }

  async function getSiteData() {
    setIsLogoAnimationLoading(true);

    try {
      const res = await DotNetAPI.get(`hireable-items/${itemID}/app`);
      const response = res?.data || {};

      if (
        Object.prototype.hasOwnProperty.call(response, 'status') &&
        !apiStatusIsSuccess(response)
      ) {
        setAuthFlow(AuthFlow.NO_LOCK);
        setErrorMsg(getApiMessage(response, 'Could not load venue.'));
        setInvalidLock(true);
        setLogoAnimation(null);
        setHasReportDamage(false);
        return;
      }

      const payload = response?.data || response;

      if (!payload || typeof payload !== 'object') {
        setAuthFlow(AuthFlow.NO_LOCK);
        setErrorMsg('Could not load venue.');
        setInvalidLock(true);
        setLogoAnimation(null);
        setHasReportDamage(false);
        return;
      }

      const nextBusinessId = toNumber(
        payload.business_id
          ?? payload.businessId
          ?? payload.site?.business_id
          ?? payload.site?.businessId
          ?? payload.item?.business_id
          ?? payload.item?.businessId,
        0,
      );

      if (nextBusinessId > 0) {
        setBusinessId(nextBusinessId);
      }

      applyLoadedSitePayload(payload, nextBusinessId);
    } catch (err) {
      setAuthFlow(AuthFlow.NO_LOCK);
      setErrorMsg(err?.response?.data?.message || err?.message || 'Could not load venue.');
      setInvalidLock(true);
      setLogoAnimation(null);
      setHasReportDamage(false);
    } finally {
      setIsLogoAnimationLoading(false);
    }
  }

  async function requestLocationCheck() {
    if (CONFIG.devMode || isCareZenBusiness) {
      setLocationErrorCode(null);
      setGeoPermissionState('granted');
      setErrorMsg('');
      setAuthFlow(AuthFlow.INPUT_DETAILS);
      return true;
    }

    const lat = Number(site?.lat);
    const lng = Number(site?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      console.warn('[Location] Missing site lat/lng');
      setLocationErrorCode(null);
      setErrorMsg('This site does not have a valid location configured.');
      setAuthFlow(AuthFlow.NO_LOCATION);
      return false;
    }

    await syncGeolocationPermission();

    try {
      const { pos, coarse } = await getBestEffortPosition();

      const coords = pos.coords || {};
      const user = { lat: coords.latitude, lng: coords.longitude };
      const target = { lat, lng };

      const { ok } = isWithinAllowedRadius({
        user,
        target,
        accuracy: coords.accuracy,
        coarse,
      });

      setLocationErrorCode(null);
      setGeoPermissionState('granted');

      if (ok) {
        setErrorMsg('');
        setAuthFlow(AuthFlow.INPUT_DETAILS);
        return true;
      }

      setErrorMsg('');
      setAuthFlow(AuthFlow.NOT_NEAR_LOCATION);
      return false;
    } catch (err) {
      console.warn('[Location] best-effort failed:', err);

      const latestPermissionState = await syncGeolocationPermission();
      setLocationErrorCode(err?.code ?? null);

      if (err?.code === 1 || latestPermissionState === 'denied') {
        setErrorMsg(
          'Location access was denied. Please change this browser’s location permission to Ask or Allow, then tap Try again.'
        );
      } else if (err?.code === 2) {
        setErrorMsg(
          'We could not determine your location. Please make sure Location Services are enabled on your device, then try again.'
        );
      } else if (err?.code === 3) {
        setErrorMsg(
          'Location request timed out. Please move to an area with better signal and try again.'
        );
      } else {
        setErrorMsg(
          'We could not get your location. Please check your device and browser location settings, then try again.'
        );
      }

      setAuthFlow(AuthFlow.NO_LOCATION);
      return false;
    }
  }

  function getAuthBody(twoFa = '') {
    const body = {
      phone: formatPhoneNumber(phoneNumber),
      business_id: businessId,
    };

    if (twoFa) {
      body.two_fa = String(twoFa).trim();
    }

    return body;
  }

  function getUnlockBody() {
    return {
      item_id: Number(itemID),
      name: name.trim() === '' ? 'Opt Out' : name.trim(),
      phone: formatPhoneNumber(phoneNumber),
      tz: moment.tz.guess(),
      business_id: businessId,
      hire_length: toNumber(lock?.hire_length, 0),
      session_id: sessionId || 0,
    };
  }

  function handleBannedUser(user = {}) {
    if (!truthy(user?.is_banned)) {
      return false;
    }

    setAuthFlow(AuthFlow.INPUT_DETAILS);

    if (businessTitle !== '') {
      setErrorMsg(`Your account has been banned by ${businessTitle}, please contact ${businessTitle} for more information`);
    } else {
      setErrorMsg('Your account has been banned, please contact support for more information');
    }

    setIsBanned(true);

    return true;
  }

  async function unlockCurrentItem() {
    const res = await DotNetAPI.put(
      `hireable-items/${itemID}/customer-unlock`,
      getUnlockBody(),
    );

    const json = res?.data || {};
    const data = json?.data || {};

    if (!apiStatusIsSuccess(json)) {
      throw new Error(getApiMessage(json, 'Failed to unlock.'));
    }

    if (truthy(data.is_hired_by_another)) {
      throw new Error('This facility has been hired by another person. Please try again later.');
    }

    return json;
  }

  async function handleUnlockClick() {
    if (!phoneNumber) {
      setErrorMsg('Phone number is required.');
      return;
    }

    if (!businessId) {
      setErrorMsg('Still loading venue. Please wait a second and try again.');
      return;
    }

    setErrorMsg('');
    setVerificationCodeErr('');
    setAuthFlow(AuthFlow.LOADING);

    const submittedName = name.trim() === '' ? 'Opt Out' : name;

    try {
      if (!shouldUseDotNetCustomerFlow) {
        const response = await legacyCheckUserAuth(
          itemID,
          submittedName,
          phoneNumber,
          '',
          businessId,
        );

        const data = response?.data || {};

        if (truthy(data.is_banned)) {
          handleBannedUser({ is_banned: true });
          return;
        }

        if (truthy(data.is_hired_by_another)) {
          setAuthFlow(AuthFlow.INPUT_DETAILS);
          setErrorMsg('This facility has been hired by another person. Please try again later.');
          return;
        }

        if (lock?.is_ajax_item && truthy(data.has_session) && data.session) {
          const sess = data.session;

          setSessionId(sess.id);
          setSessionLength(Number(sess.length || 0));

          const remainSec = Number(sess.remaining_seconds ?? sess.remain_seconds ?? 0);
          const started = truthy(sess.is_started) || truthy(sess.started);

          if (started) {
            setIsHireStarted(true);
            setRemainingTime(remainSec);
          } else {
            setIsHireStarted(false);
            setRemainingTime(Number(sess.length || 0) * 60);
          }

          setAuthFlow(AuthFlow.UNLOCKED);
          return;
        }

        if (truthy(data.auth)) {
          if (lock?.is_ajax_item) {
            setHireMode('initial');
            setAuthFlow(AuthFlow.HIRE_SELECT);
          } else {
            setShowConfetti(true);
            setAuthFlow(AuthFlow.UNLOCKED);
          }
        } else {
          setAuthFlow(AuthFlow.ENTER_OTP);
        }

        return;
      }

      const res = await DotNetAPI.post(
        'auth/customer',
        getAuthBody(),
      );

      const json = res?.data || {};

      if (!apiStatusIsSuccess(json)) {
        setAuthFlow(AuthFlow.INPUT_DETAILS);
        setErrorMsg(getApiMessage(json, 'Failed to send verification code.'));
        return;
      }

      const data = json?.data || {};

      if (handleBannedUser(data?.user)) {
        return;
      }

      setAuthFlow(AuthFlow.ENTER_OTP);
    } catch (err) {
      setAuthFlow(AuthFlow.INPUT_DETAILS);
      setErrorMsg(`Failed to authenticate: ${err?.response?.data?.message || err.message || err.toString()}`);
    }
  }

  async function handleConfirmOTP(codeEnteredByUser) {
    if (!codeEnteredByUser) {
      setVerificationCodeErr('Verification code is required.');
      setAuthFlow(AuthFlow.ENTER_OTP);
      return;
    }

    if (!businessId) {
      setVerificationCodeErr('Still loading venue. Please wait a second and try again.');
      setAuthFlow(AuthFlow.ENTER_OTP);
      return;
    }

    setVerificationCodeErr('');
    setAuthFlow(AuthFlow.LOADING);

    try {
      if (!shouldUseDotNetCustomerFlow) {
        const compareRes = await legacyConfirmVerificationCode(
          itemID,
          name,
          phoneNumber,
          codeEnteredByUser,
          businessId,
        );

        const ok = truthy(compareRes?.data?.auth);

        if (!ok) {
          setVerificationCodeErr('Invalid verification code.');
          setAuthFlow(AuthFlow.ENTER_OTP);
          return;
        }

        setShowConfetti(true);

        if (lock?.is_ajax_item) {
          setHireMode('initial');
          setAuthFlow(AuthFlow.HIRE_SELECT);
        } else {
          setAuthFlow(AuthFlow.UNLOCKED);
        }

        return;
      }

      const verifyRes = await DotNetAPI.post(
        'auth/customer/verify',
        getAuthBody(codeEnteredByUser),
      );

      const verifyJson = verifyRes?.data || {};
      const verifyData = verifyJson?.data || {};

      if (!apiStatusIsSuccess(verifyJson) || !truthy(verifyData.correct)) {
        setVerificationCodeErr(getApiMessage(verifyJson, 'Invalid verification code.'));
        setAuthFlow(AuthFlow.ENTER_OTP);
        return;
      }

      if (handleBannedUser(verifyData?.user)) {
        return;
      }

      await unlockCurrentItem();

      setShowConfetti(true);
      setRemainingTime(toNumber(lock?.hire_max_length, 5) * 60 || 300);
      setAuthFlow(AuthFlow.UNLOCKED);
    } catch (err) {
      setVerificationCodeErr(`Verification error: ${err?.response?.data?.message || err.message || err.toString()}`);
      setAuthFlow(AuthFlow.ENTER_OTP);
    }
  }

  function handleSessionReady({ sessionId: sid, minutes, length }) {
    setSessionId(sid);
    setSessionLength(length || minutes || 0);
    setIsHireStarted(false);
    setRemainingTime((length || minutes || 0) * 60);
    setAuthFlow(AuthFlow.UNLOCKED);
  }

  async function onStartHire() {
    if (!sessionId) {
      setErrorMsg('No session available to start.');
      return;
    }

    setStartingHire(true);
    setErrorMsg('');

    try {
      const res = await activateHiredSession({
        itemID,
        phone: phoneNumber,
        sessionId,
        businessID: businessId,
      });

      const ok = truthy(res?.data?.is_started) || truthy(res?.data?.started);

      if (ok) {
        setIsHireStarted(true);
        if (!timerId) startCountdown();
      } else {
        setErrorMsg('Failed to start hire, please try again.');
      }
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to start hire.');
    } finally {
      setStartingHire(false);
    }
  }

  async function onEndHire() {
    if (!sessionId) {
      setErrorMsg('No session available to start.');
      return;
    }

    try {
      await deactivateHiredSession({ itemID, sessionId });
    } catch (e) {
      setErrorMsg(e?.message || 'Failed to end hire.');
    } finally {
      setStartingHire(false);
    }
  }

  async function onUnlockAgain() {
    setUnlocking(true);
    setErrorMsg('');

    try {
      if (!shouldUseDotNetCustomerFlow) {
        const unlockRes = await legacyUnlockAgain(
          itemID,
          name,
          phoneNumber,
          '',
          businessId,
        );

        if (!unlockRes?.status) {
          setErrorMsg('Could not unlock again. Please try again.');
        }

        return;
      }

      await unlockCurrentItem();
      setRemainingTime(toNumber(lock?.hire_max_length, 5) * 60 || 300);
    } catch (err) {
      setErrorMsg(`Unlock failed: ${err?.response?.data?.message || err.message || err.toString()}`);
    } finally {
      setUnlocking(false);
    }
  }



  if (isOutage) {
    return (
      <Container size={700} px="md" style={{ height: '100vh' }}>
        <Center style={S.fullHeightCenter}>
          <FaTools size={100} />

          <Text size="lg" fw="bold" ta="center">
            This item is currently under maintenance.
          </Text>
        </Center>
      </Container>
    );
  }

  let content;

  switch (authFlow) {
    case AuthFlow.NO_LOCK:
      content = isMembershipOnly ? (
        <MembershipOnlyView
          title="Members-only access"
          message={errorMsg || 'This facility can only be accessed by member users. Please download our full app to access this.'}
          appleLink={appStoreLink}
          googleLink={googlePlayStoreLink}
          logoAnimation={logoAnimation}
          isLogoAnimationLoading={isLogoAnimationLoading}
        />
      ) : (
        <NoLockView
          title={invalidLock ? 'Item Not Found' : 'No App Clip Scanned'}
          message={
            invalidLock
              ? errorMsg
              : "You haven't scanned a QR code yet. Please scan the QR code on the smart lock to begin your access."
          }
        />
      );
      break;

    case AuthFlow.NO_LOCATION:
      content = (
        <NoLocationView
          onRetry={requestLocationCheck}
          browserInfo={browserInfo}
          permissionState={geoPermissionState}
          locationErrorCode={locationErrorCode}
        />
      );
      break;

    case AuthFlow.EXPIRED:
      content = <ExpiredView />;
      break;

    case AuthFlow.UNKNOWN:
      content = (
        <Container size={700} px="md" style={{ height: '100vh' }}>
          <Center style={S.fullHeightCenter}>
            <Loader />
          </Center>
        </Container>
      );
      break;

    case AuthFlow.LOADING:
      content = <LoadingView />;
      break;

    case AuthFlow.NOT_NEAR_LOCATION:
      content = <NotNearLocationView />;
      break;

    case AuthFlow.INPUT_DETAILS:
      content = (
        <InputDetailsView
          site={site}
          lock={lock}
          isCareZenBusiness={isCareZenBusiness}
          isBanned={isBanned}
          logoAnimation={logoAnimation}
          isLogoAnimationLoading={isLogoAnimationLoading}
          name={name}
          setName={setName}
          phoneNumber={phoneNumber}
          setPhoneNumber={setPhoneNumber}
          errorMsg={errorMsg}
          supportPhone={supportPhone}
          onSubmit={handleUnlockClick}
        />
      );
      break;

    case AuthFlow.HIRE_SELECT:
      content = (
        <StartHireView
          itemID={itemID}
          userPhoneNumber={phoneNumber}
          maxMinutes={
            hireMode === 'extend'
              ? Math.max(0, Number(lock?.hire_max_length || 0) - Number(sessionLength || 0))
              : Number(lock?.hire_max_length || 0)
          }
          costPerMinute={lock?.hire_cost_base}
          isExtension={hireMode === 'extend'}
          sessionId={sessionId}
          alreadyPurchasedMinutes={sessionLength}
          businessID={businessId}
          stripePublishableKey={stripePublishableKey}
          onSessionReady={handleSessionReady}
          onExtended={({ newTotalMinutes, remainingSeconds }) => {
            setSessionLength(newTotalMinutes);
            setRemainingTime(remainingSeconds);
            setAuthFlow(AuthFlow.UNLOCKED);
          }}
          onBack={() => setAuthFlow(AuthFlow.UNLOCKED)}
        />
      );
      break;

    case AuthFlow.ENTER_OTP:
      content = (
        <EnterOtpView
          onConfirmOTP={handleConfirmOTP}
          verificationCodeErr={verificationCodeErr}
        />
      );
      break;

    case AuthFlow.UNLOCKED:
      content = (
        <UnlockedView
          isAjaxItem={!!lock?.is_ajax_item}
          isHireStarted={isHireStarted}
          onStartHire={onStartHire}
          startingHire={startingHire}
          onUnlockAgain={onUnlockAgain}
          unlocking={unlocking}
          remainingTime={remainingTime}
          hireMaxLength={lock?.hire_max_length}
          sessionPurchasedTime={sessionLength}
          errorMsg={errorMsg}
          setAuthFlow={setAuthFlow}
          AuthFlowRef={AuthFlow}
          showConfetti={showConfetti}
          setHireMode={setHireMode}
          supportPhone={supportPhone}
          hasReportDamage={hasReportDamage}
          onReportDamage={() => {
            if (hasReportDamage) {
              setShowReportDamage(true);
            }
          }}
        />
      );
      break;

    default:
      content = (
        <Container size={700} px="md">
          <Center style={S.fullHeightCenter}>
            <Text>Unhandled state: {authFlow}</Text>
          </Center>
        </Container>
      );
      break;
  }

  return (
    <>
      {content}

      <ReportDamageFlow
        opened={showReportDamage && hasReportDamage}
        onClose={() => setShowReportDamage(false)}
        siteID={siteID}
        itemID={itemID}
        phone={phoneNumber}
        businessID={businessId}
      />
    </>
  );
}